import React from "react"

import { Button, Grid, FormControl, TextField } from "@material-ui/core"

import { Trackablelink, TrackablelinkResponse } from "@/protocols/trackableLinks"
import { Notification, Portlet } from "@/components"
import TrackableLinkTitleOnTable from "@/pages/Admin/TrackableLinks/TrackableLinksTable/TrackableLinkTitleOnTable"
import TrackableLinksTableSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinksTable/TrackableLinksTableSkeleton"
import CopyTrackableLinkTableButton from "@/pages/Admin/TrackableLinks/TrackableLinksTable/CopyTrackableLinkTableButton"
import RedirectTrackableLinkMetricsTableButton from "@/pages/Admin/TrackableLinks/TrackableLinksTable/RedirectTrackableLinkMetricsTableButton"
import EmptyTrackableLinksList from "@/pages/Admin/TrackableLinks/EmptyTrackableLinksList"
import ActionsDropdown from "@/pages/Admin/TrackableLinks/ActionsDropdown"
import DataTableWithPagination from "@/components/DataTableWithPagination"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import { TableColumn } from "@/components/DataTable"
import { fullDatetime } from "@/utils/time"
import { mountTrackableLinkURL, mountTrackableLinkMetricsPagePath } from "@/utils/trackableLinks"
import { inboxAppUrl } from "@/config/url"

export type Filters = {
	search: string | null
}

export type TitleLink = {
	text: string
	link: string
}

export type FormattedLinkToCopy = {
	linkToBeCopied: string
	text: string
}

type ActionsType = {
	trackableLinkId: number
	copyLinkUrl: string
}

enum SearchStep {
	IS_FILTERING,
	NO_RESULT_FOUND,
	NO_SEARCH
}

export type FormattedTrackableLinksDataResponse = {
	titleLink: TitleLink
	formattedLinkToCopy: FormattedLinkToCopy
	redirectToMetrics: string
	createdAt: string
	actions: ActionsType
}

type FormattedTrackableLinksData = {
	trackableLinks: FormattedTrackableLinksDataResponse[]
	count: number
}

const trackableLinksTableColumns: TableColumn<FormattedTrackableLinksDataResponse>[] = [
	{ field: "titleLink", label: "Título", rowRender: (titleLink) => <TrackableLinkTitleOnTable titleLink={titleLink as TitleLink} /> },
	{ field: "formattedLinkToCopy", label: "Link", rowRender: (formattedLinkToCopy) => <CopyTrackableLinkTableButton formattedLinkToCopy={formattedLinkToCopy as FormattedLinkToCopy} /> },
	{ field: "redirectToMetrics", label: "Métricas", rowRender: (redirectLink) => <RedirectTrackableLinkMetricsTableButton redirectLink={redirectLink as string} /> },
	{ field: "createdAt", label: "Criado em" },
	{ field: "actions", label: "Ações", rowRender: (action) => <ActionsDropdown trackableLinkId={(action as ActionsType).trackableLinkId} copyLinkUrl={(action as ActionsType).copyLinkUrl} /> }
]

const TrackableLinksTable: React.FC = () => {
	const [paginationState, setPaginationState] = React.useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [searchStep, setSearchStep] = React.useState(SearchStep.NO_SEARCH)
	const [loading, setLoading] = React.useState(false)
	const [links, setLinks] = React.useState<FormattedTrackableLinksData>({
		trackableLinks: [],
		count: 0
	})
	const [filters, setFilters] = React.useState<Filters>({
		search: null
	})

	const { triggerValidation } = useValidation()

	const showEmptyTrackableLinksList = searchStep === SearchStep.NO_SEARCH && !loading && links.count === 0

	const formatTrackableLinksData = (data: Trackablelink[]): FormattedTrackableLinksDataResponse[] => {
		const formattedData = data.map((link) => {
			const linkToBeCopied = mountTrackableLinkURL(link.hash, link.slug)
			const linkToRedirectToMetrics = mountTrackableLinkMetricsPagePath(link.id?.toString())

			return {
				titleLink: {
					text: link.title,
					link: linkToRedirectToMetrics
				},
				formattedLinkToCopy: {
					linkToBeCopied,
					text: `${inboxAppUrl.trackableLinkURL}/${link.hash}/${link.slug}`
				},
				redirectToMetrics: linkToRedirectToMetrics,
				createdAt: fullDatetime(new Date(link.createdAt)),
				actions: {
					trackableLinkId: link.id,
					copyLinkUrl: linkToBeCopied
				}
			}
		})

		return formattedData
	}

	const handleGetTrackableLinks = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackablelinkResponse>("/trackable-links", {
				params: {
					search: filters.search,
					rowsPerPage: paginationState.rowsPerPage,
					page: paginationState.page
				}
			})
			const formattedData = formatTrackableLinksData(data.trackablelinks)

			setLinks({
				trackableLinks: formattedData,
				count: data.count
			})
			setLoading(false)

			return data
		} catch (error) {
			setLoading(false)
			Notification.error({ message: "Ocorreu um erro ao buscar os links rastreáveis" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleChangeEditFilters = (data: Partial<Filters>) => {
		setSearchStep(SearchStep.IS_FILTERING)
		setFilters({
			...filters,
			...data
		})
	}

	const handlePaginationState = (data: Partial<DefaultWhereDataType>) => {
		setSearchStep(filters.search !== null ? SearchStep.IS_FILTERING : SearchStep.NO_SEARCH)
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleApplyFilters = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		const data = await handleGetTrackableLinks()
		setSearchStep(data?.count === 0 ? SearchStep.NO_RESULT_FOUND : SearchStep.IS_FILTERING)
	}

	React.useEffect(() => {
		handleGetTrackableLinks()
	}, [paginationState])

	return (
		<>
			{showEmptyTrackableLinksList ? (
				<EmptyTrackableLinksList />
			) : (
				<Portlet>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<form onSubmit={handleApplyFilters}>
								<Grid container alignItems="center" spacing={1}>
									<Grid item xs={4}>
										<FormControl fullWidth>
											<TextField
												value={filters.search}
												onChange={
													({ target }) => handleChangeEditFilters({ search: target.value?.trimStart() })
												}
												size="medium"
												placeholder="Busque por um título"
												fullWidth
												variant="outlined"
											/>
										</FormControl>
									</Grid>
									<Grid item>
										<Button
											variant="contained"
											color="primary"
											type="submit"
										>
												Buscar
										</Button>
									</Grid>
								</Grid>
							</form>
						</Grid>

						{loading ? (
							<Grid item xs={12}>
								<TrackableLinksTableSkeleton columnList={trackableLinksTableColumns} />
							</Grid>
						) : (
							<Grid item xs={12}>
								<DataTableWithPagination
									columns={trackableLinksTableColumns}
									data={links.trackableLinks}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={links.count}
									onPaginationChange={handlePaginationState}
								/>
							</Grid>
						)}
					</Grid>
				</Portlet>
			)}
		</>
	)
}

export default TrackableLinksTable
